import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { num } from "@owners/format";
import { api, downloadPost } from "../api";
import { DocLinks, FilterButtons, SegmentBar, StatusPill, needsAttention, periodLabel, shortDate, type Item } from "../components/Items";

type Folder = "reo_documents" | "compliance" | "debt_compliance";
interface HotelRow { id: string; name: string; gm: string | null; items: Item[] }

const TITLES: Record<Folder, { eyebrow: string; title: string }> = {
  reo_documents: { eyebrow: "OWNERS MODULE · REO DOCUMENTS", title: "Ownership & loans" },
  compliance: { eyebrow: "OWNERS MODULE · COMPLIANCE", title: "Licenses & permits" },
  debt_compliance: { eyebrow: "OWNERS MODULE · DEBT COMPLIANCE", title: "Loan documents" },
};

type Filter = "all" | "attention" | "pip" | "expiring" | "expired" | "missing";
const FILTER_LABELS: Record<Filter, string> = {
  all: "All", attention: "Needs attention", pip: "Open PIPs", expiring: "Expiring in 30 days", expired: "Expired", missing: "No document",
};
const hasPip = (h: HotelRow) => h.items.some((i) => i.docType === "pip" && i.document);
const FILTERS: Record<Filter, (h: HotelRow) => boolean> = {
  all: () => true,
  attention: (h) => h.items.some((i) => needsAttention(i.status)),
  pip: hasPip,
  // "expiring" is the API's 30-day window (SPEC §8: yellow 30 days before expiry).
  expiring: (h) => h.items.some((i) => i.status === "expiring" || i.status === "due"),
  expired: (h) => h.items.some((i) => i.status === "expired"),
  missing: (h) => h.items.some((i) => i.status === "missing"),
};

/**
 * REO Documents (SPEC §7) and Compliance (§8): one expandable row per hotel,
 * items grouped, status pill + segment bar, View/Get per item. Data comes from
 * the Dataroom. Sources are issuing parties — never a software system.
 */
export function FolderStatus({ folder }: { folder: Folder }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<string | null>(null);
  // Select downloads (demo): tick documents (or a whole hotel), get one zip.
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false), [err, setErr] = useState<string | null>(null);
  const docIds = (h: HotelRow) => h.items.filter((i) => i.document).map((i) => i.document!.id);
  const toggle = (ids: string[], on: boolean) => setSel((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
  const download = async () => {
    setBusy(true); setErr(null);
    try {
      await downloadPost("/api/documents/zip", { documentIds: [...sel], name: `${TITLES[folder].title} ${new Date().toISOString().slice(0, 10)}` }, "documents.zip");
      setSel(new Set()); setSelecting(false);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const q = useQuery({ queryKey: ["folder", folder], queryFn: () => api<{ hotels: HotelRow[]; updatedAt: string }>(`/api/folders/${folder}`) });
  const all = q.data?.hotels ?? [];
  const hotels = all.filter((h) => FILTERS[filter](h));
  const updated = q.data ? new Date(q.data.updatedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
  const keys: Filter[] = folder === "reo_documents" ? ["all", "attention", "pip"]
    : folder === "compliance" ? ["all", "attention", "expiring", "expired", "missing"]
    : ["all", "attention"];

  return (
    <>
      <div className="eyebrow">{TITLES[folder].eyebrow}</div>
      <h1>{TITLES[folder].title}</h1>
      <p className="muted">{num(all.length)} hotels · updated today at {updated} · click a hotel, then any item to see the document</p>
      {/* Demo: chips carry counts ("All · 33", "Needs attention · 8"). */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <FilterButtons value={filter} onChange={setFilter}
          options={keys.map((k): [Filter, string] => [k, `${FILTER_LABELS[k]} · ${num(all.filter(FILTERS[k]).length)}`])} />
        <button className={`btn ${selecting ? "" : "secondary"}`} onClick={() => { setSelecting(!selecting); setSel(new Set()); setErr(null); }}>
          {selecting ? "Cancel" : "Select downloads"}
        </button>
      </div>
      {selecting && (
        <div className="select-bar">
          <span>{sel.size ? `${num(sel.size)} document${sel.size === 1 ? "" : "s"} selected` : "Tick documents, or a hotel to take all of its documents"}</span>
          <button className="link" onClick={() => toggle(hotels.flatMap(docIds), true)}>Select all shown</button>
          {sel.size > 0 && <button className="link" onClick={() => setSel(new Set())}>Clear</button>}
          <button className="btn gold" style={{ marginLeft: "auto" }} disabled={!sel.size || sel.size > 100 || busy} onClick={download}>
            {busy ? "Preparing zip…" : sel.size > 100 ? "Up to 100 at a time" : "Download zip"}
          </button>
          {err && <span className="error">{err}</span>}
        </div>
      )}
      {q.isLoading && <p className="muted">Loading…</p>}
      {hotels.map((h) => {
        const attention = h.items.filter((i) => needsAttention(i.status));
        const missing = h.items.filter((i) => i.status === "missing").length;
        return (
          <div className="card" key={h.id} style={{ padding: 0, display: "flex", alignItems: "flex-start", flexWrap: "wrap" }}>
            {selecting && (() => {
              const ids = docIds(h), n = ids.filter((id) => sel.has(id)).length;
              return <input type="checkbox" style={{ margin: "18px 0 0 16px" }} disabled={!ids.length} title={ids.length ? `All ${ids.length} documents for this hotel` : "No documents on file"}
                checked={!!ids.length && n === ids.length} ref={(el) => { if (el) el.indeterminate = n > 0 && n < ids.length; }}
                onChange={(e) => toggle(ids, e.target.checked)} />;
            })()}
            <button onClick={() => setOpen(open === h.id ? null : h.id)}
              style={{ all: "unset", cursor: "pointer", display: "flex", gap: 16, alignItems: "center", padding: "14px 20px", flex: 1, minWidth: 0 }}>
              <span style={{ flex: 1 }}>
                <strong style={{ fontFamily: "var(--serif)", color: "var(--navy)", fontSize: 16 }}>{h.name}</strong>
                {h.gm && <span className="muted" style={{ fontSize: 12, marginLeft: 10 }}>GM {h.gm}</span>}
              </span>
              {folder === "reo_documents" && hasPip(h) && <span className="pill warn-pill">PIP on file</span>}
              {missing > 0 && <span className="pill" style={{ color: "#b42318" }}>{num(missing)} missing</span>}
              <SegmentBar items={h.items} />
              <span className="muted" style={{ fontSize: 12 }}>
                {num(h.items.length - attention.length)} current · {num(attention.length)} need attention
              </span>
            </button>
            {open === h.id && <div style={{ flexBasis: "100%" }}><HotelItems h={h} sel={selecting ? sel : null} onToggle={(id, on) => toggle([id], on)} /></div>}
          </div>
        );
      })}
    </>
  );
}

function HotelItems({ h, sel, onToggle }: { h: HotelRow; sel: Set<string> | null; onToggle: (id: string, on: boolean) => void }) {
  const groups = new Map<string, Item[]>();
  for (const i of h.items) {
    const g = i.group ?? "Documents";
    groups.set(g, [...(groups.get(g) ?? []), i]);
  }
  return (
    <div style={{ padding: "0 20px 14px" }}>
      {[...groups.entries()].map(([g, items]) => (
        <div key={g} style={{ marginBottom: 12 }}>
          <div className="eyebrow" style={{ margin: "10px 0 4px" }}>{g}</div>
          <table>
            <thead><tr>{sel && <th />}<th className="num">#</th><th>Item</th><th>Issued by</th><th>Date</th><th>Expires / next due</th><th>Status</th><th /></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.requirementId}>
                  {sel && <td>{i.document && <input type="checkbox" checked={sel.has(i.document.id)} onChange={(e) => onToggle(i.document!.id, e.target.checked)} />}</td>}
                  <td className="num muted">{num(h.items.indexOf(i) + 1)}</td>
                  <td>{i.label}{i.period ? ` · ${periodLabel(i.period)}` : ""}</td>
                  <td>{i.document?.issuedBy ?? "—"}</td>
                  <td>{shortDate(i.document?.documentDate ?? i.document?.uploadedAt)}</td>
                  <td>{i.document?.expiresOn ? shortDate(i.document.expiresOn) : i.dueOn ? shortDate(i.dueOn) : "—"}</td>
                  <td><StatusPill s={i.status} dueOn={i.dueOn} /></td>
                  <td>{i.document ? <DocLinks id={i.document.id} /> : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
